import React, { useState, useRef, useEffect } from "react";
import "./Preview.css";
import { DEVICES, DEVICE_SIZES } from "../../constants/breakpoints";
import CMSContentElement from "../CMS/CMSContentElement";
import ElementSettingsPopup from "./ElementSettingsPopup";

// Add stylesheet references
let previewStyleSheetRef = null;
let previewCustomCssStyleSheetRef = null;

// Add stylesheet creation functions
const createPreviewStyleSheet = (classStylesRegistry, combinedClassStylesRegistry) => {
  if (!previewStyleSheetRef) {
    previewStyleSheetRef = document.createElement('style');
    previewStyleSheetRef.id = 'editor-preview-css';
    document.head.appendChild(previewStyleSheetRef);
  }

  let cssRules = '';

  // Add animation keyframes at the start
  cssRules += `
    /* Animation Keyframes */
    @keyframes fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slide-up {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes slide-down {
      from {
        transform: translateY(-50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes bounce {
      0%, 100% { 
        transform: scale(1);
        opacity: 1;
      }
      50% { 
        transform: scale(1.1);
        opacity: 1;
      }
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-10px); }
      75% { transform: translateX(10px); }
    }
  `;

  // Generate rules for each device
  const generateDeviceRules = (device) => {
    let rules = '';
    
    // Add animation trigger rules
    rules += `
      /* Animation trigger rules */
      [data-animation-trigger="onView"] {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }

      [data-animation-trigger="onView"].in-view {
        opacity: 1;
        visibility: visible;
      }

      [data-animation-trigger="onHover"]:hover {
        animation-play-state: running !important;
      }

      [data-animation-trigger="onLoad"] {
        animation-play-state: running !important;
      }

      [data-animation-trigger="onClick"]:active {
        animation-play-state: running !important;
      }
    `;

    // Get all unique class names across all devices
    const allClassNames = new Set();
    Object.values(classStylesRegistry).forEach(registry => {
      registry.forEach((_, className) => allClassNames.add(className));
    });

    // Group classes by their base name
    const classGroups = {};
    allClassNames.forEach(className => {
      if (className.includes('--')) {
        const [baseClass] = className.split('--');
        if (!classGroups[baseClass]) {
          classGroups[baseClass] = { base: null, states: new Set() };
        }
        classGroups[baseClass].states.add(className);
      } else {
        if (!classGroups[className]) {
          classGroups[className] = { base: className, states: new Set() };
        } else {
          classGroups[className].base = className;
        }
      }
    });

    // Get all unique combined class names across all devices
    const allCombinedClassNames = new Set();
    Object.values(combinedClassStylesRegistry).forEach(registry => {
      registry.forEach((_, className) => allCombinedClassNames.add(className));
    });

    // Group combined classes by their base combination
    const combinedClassGroups = {};
    allCombinedClassNames.forEach(className => {
      if (className.includes('--')) {
        const [baseClass] = className.split('--');
        if (!combinedClassGroups[baseClass]) {
          combinedClassGroups[baseClass] = { base: null, states: new Set() };
        }
        combinedClassGroups[baseClass].states.add(className);
      } else {
        if (!combinedClassGroups[className]) {
          combinedClassGroups[className] = { base: className, states: new Set() };
        } else {
          combinedClassGroups[className].base = className;
        }
      }
    });

    // Generate rules for single classes
    Object.entries(classGroups).forEach(([baseClass, { base, states }]) => {
      // Handle base styles
      let baseStyles = {};
      if (device === DEVICES.MOBILE) {
        const tabletStyles = classStylesRegistry[DEVICES.TABLET]?.get(base) || {};
        const mobileStyles = classStylesRegistry[DEVICES.MOBILE]?.get(base) || {};
        baseStyles = { ...tabletStyles, ...mobileStyles };
      } else {
        baseStyles = classStylesRegistry[device]?.get(base) || {};
      }

      // Generate base class rules
      if (base) {
        const cssProperties = Object.entries(baseStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        rules += `
          .${base} {
            ${cssProperties}
          }
        `;
      }

      // Handle state styles for single classes
      states.forEach(stateClass => {
        const [, state] = stateClass.split('--');
        const pseudoClass = state === 'pressed' ? 'active' : 
                           state === 'focused' ? 'focus' : 
                           state;
        
        let stateStyles = {};
        if (device === DEVICES.MOBILE) {
          const tabletStateStyles = classStylesRegistry[DEVICES.TABLET]?.get(stateClass) || {};
          const mobileStateStyles = classStylesRegistry[DEVICES.MOBILE]?.get(stateClass) || {};
          stateStyles = { ...tabletStateStyles, ...mobileStateStyles };
        } else {
          stateStyles = classStylesRegistry[device]?.get(stateClass) || {};
        }

        const cssProperties = Object.entries(stateStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        // Standard pseudo-class rule
        rules += `
          .${baseClass}:${pseudoClass} {
            ${cssProperties}
            ${Object.keys(stateStyles).length > 0 ? 'transition: all 0.3s ease;' : ''}
          }
        `;

        // Add rule for forced state using data-force-state attribute
        rules += `
          .${baseClass}[data-force-state="${state}"] {
            ${cssProperties}
          }
        `;
      });
    });

    // Generate rules for combined classes
    Object.entries(combinedClassGroups).forEach(([baseCombination, { base, states }]) => {
      // Handle base styles for combined classes
      let baseCombinedStyles = {};
      if (device === DEVICES.MOBILE) {
        const tabletStyles = combinedClassStylesRegistry[DEVICES.TABLET]?.get(base) || {};
        const mobileStyles = combinedClassStylesRegistry[DEVICES.MOBILE]?.get(base) || {};
        baseCombinedStyles = { ...tabletStyles, ...mobileStyles };
      } else {
        baseCombinedStyles = combinedClassStylesRegistry[device]?.get(base) || {};
      }

      // Generate base combined class rules
      if (base) {
        const combinedSelectors = base.split('+').map(c => `.${c}`).join('');
        const cssProperties = Object.entries(baseCombinedStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        rules += `
          ${combinedSelectors} {
            ${cssProperties}
          }
        `;
      }

      // Handle state styles for combined classes
      states.forEach(stateClass => {
        const [, state] = stateClass.split('--');
        const pseudoClass = state === 'pressed' ? 'active' : 
                           state === 'focused' ? 'focus' : 
                           state;
        
        let stateCombinedStyles = {};
        if (device === DEVICES.MOBILE) {
          const tabletStateStyles = combinedClassStylesRegistry[DEVICES.TABLET]?.get(stateClass) || {};
          const mobileStateStyles = combinedClassStylesRegistry[DEVICES.MOBILE]?.get(stateClass) || {};
          stateCombinedStyles = { ...tabletStateStyles, ...mobileStateStyles };
        } else {
          stateCombinedStyles = combinedClassStylesRegistry[device]?.get(stateClass) || {};
        }

        const combinedSelectors = baseCombination.split('+').map(c => `.${c}`).join('');
        const cssProperties = Object.entries(stateCombinedStyles).map(([prop, value]) => {
          const kebabCase = prop.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          return `${kebabCase}: ${value};`;
        }).join('\n');

        // Standard pseudo-class rule for combined classes
        rules += `
          ${combinedSelectors}:${pseudoClass} {
            ${cssProperties}
            ${Object.keys(stateCombinedStyles).length > 0 ? 'transition: all 0.3s ease;' : ''}
          }
        `;

        // Add rule for forced state using data-force-state attribute for combined classes
        const combinedForcedSelectors = baseCombination.split('+').map((c, i) => 
          i === 0 ? `.${c}[data-force-state="${state}"]` : `.${c}`
        ).join('');

        rules += `
          ${combinedForcedSelectors} {
            ${cssProperties}
          }
        `;
      });
    });

    return rules;
  };

  // Generate all styles at once with proper cascade and specificity
  cssRules += `
    /* Desktop styles (base) */
    html body .preview-content {
      ${generateDeviceRules(DEVICES.DESKTOP)}
    }

    /* Tablet styles */
    html body .preview-content[data-device="tablet"] {
      ${generateDeviceRules(DEVICES.TABLET)}
    }

    /* Mobile styles */
    html body .preview-content[data-device="mobile"] {
      ${generateDeviceRules(DEVICES.MOBILE)}
    }
  `;

  // Update stylesheet content
  previewStyleSheetRef.textContent = cssRules;
  return previewStyleSheetRef;
};

const createPreviewCustomCssStyleSheet = (elements) => {
  if (!previewCustomCssStyleSheetRef) {
    previewCustomCssStyleSheetRef = document.createElement('style');
    previewCustomCssStyleSheetRef.id = 'preview-custom-css-styles';
    document.head.appendChild(previewCustomCssStyleSheetRef);
  }

  const allRules = [];
  
  elements.forEach(element => {
    if (element['custom-css']) {
      const builderId = element.builderId;

      // Add desktop styles
      if (element['custom-css'][DEVICES.DESKTOP]) {
        allRules.push(`/* Desktop styles for ${builderId} */
html body .preview-content {
  [data-builder-id="${builderId}"] {
    ${element['custom-css'][DEVICES.DESKTOP].trim().replace(/\n/g, '\n    ')}
  }
}`);
      }

      // Add tablet styles with desktop fallback
      const tabletStyles = element['custom-css']?.[DEVICES.TABLET] || element['custom-css']?.[DEVICES.DESKTOP] || '';
      if (tabletStyles) {
        allRules.push(`/* Tablet styles for ${builderId} */
html body .preview-content[data-device="tablet"] {
  [data-builder-id="${builderId}"] {
    ${tabletStyles.trim().replace(/\n/g, '\n    ')}
  }
}`);
      }

      // Add mobile styles with tablet and desktop fallback
      const mobileStyles = element['custom-css']?.[DEVICES.MOBILE] || 
                         element['custom-css']?.[DEVICES.TABLET] || 
                         element['custom-css']?.[DEVICES.DESKTOP] || '';
      if (mobileStyles) {
        allRules.push(`/* Mobile styles for ${builderId} */
html body .preview-content[data-device="mobile"] {
  [data-builder-id="${builderId}"] {
    ${mobileStyles.trim().replace(/\n/g, '\n    ')}
  }
}`);
      }
    }
  });

  // Join all rules with newlines
  const fullCssContent = '/* Custom CSS Styles */\n' + allRules.join('\n\n');
  previewCustomCssStyleSheetRef.textContent = fullCssContent;
  return previewCustomCssStyleSheetRef;
};

// Add a function to determine what styles should be applied inline vs in stylesheet
const getElementStyles = (element, classStylesRegistry, combinedClassStylesRegistry, selectedDevice) => {
  if (!element) return {};

  //if element classname length is great then 0 then returen emplty
  if(element.className && element.className.length > 0){
    return {};
  }

  // If element has no className, return all styles inline
  if (!element.className) {
    return element.styles || {};
  }

  // If element has classes, only keep essential inline styles
  const essentialStyles = {};
  const { styles } = element;

  // List of properties that should remain inline
  const inlineProperties = [
    // 'position',
    // 'top',
    // 'left',
    // 'right',
    // 'bottom',
    // 'transform',
    // 'zIndex',
    // 'minHeight',
    // 'minWidth',
    // Add other essential properties that need to be inline
  ];

  if (styles) {
    inlineProperties.forEach(prop => {
      if (styles[prop] !== undefined) {
        essentialStyles[prop] = styles[prop];
      }
    });
  }

  return essentialStyles;
};

function Preview({
  elements,
  selectedElement,
  setSelectedElement,
  getRootElements,
  showElementSidebar,
  showHierarchySidebar,
  showAssetsSidebar,
  selectedDevice,
  setSelectedDevice,
  updateElementStyles,
  updateElementClassName,
  classStylesRegistry,
  cmsContentTypes,
  cmsContent,
  combinedClassStylesRegistry,
  setElements,
  addNewElement,
  updateElementState,
  selectedContentItem,
  updateElementText,
  setShowAssetsSidebar,
  newlyAddedElement,
  setNewlyAddedElement,
  zoomLevel,
  setZoomLevel,
  currentPageId,
  pages
}) {
  const containerRef = useRef(null);
  const resizeRef = useRef({
    startX: 0,
    startWidth: 0,
    direction: null
  });
  const [hasManualResize, setHasManualResize] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);
  const debounceTimerRef = useRef(null);
  const [draggedElementType, setDraggedElementType] = useState(null);
  const [draggedData, setDraggedData] = useState(null);
  const [settingsPopup, setSettingsPopup] = useState({
    isOpen: false,
    element: null,
    position: { top: 0, left: 0 }
  });
  
  // Add state for popup settings at the component level, not in renderSettingsPopup
  const [popupSettings, setPopupSettings] = useState({
    text: '',
    fontSize: '',
    fontWeight: '',
    color: '',
    textAlign: 'left'
  });

  // Add this state to track newly added elements that should display settings
  const [newlyAddedElementId, setNewlyAddedElementId] = useState(null);

  // Add ref for custom CSS style tag
  const customCssStyleRef = useRef(null);

  // Add drag and drop handler functions
  const handleDragOver = (e, element) => {
    e.preventDefault();
    e.stopPropagation();

    // Ensure we have valid event and element
    if (!e || !element || !e.currentTarget) return;

    // Store the necessary information immediately
    const currentTarget = e.currentTarget;
    const rect = currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the visual updates
    debounceTimerRef.current = setTimeout(() => {
      try {
        // Remove existing drag-over classes
        document.querySelectorAll('.drag-over').forEach(el => {
          if (el !== currentTarget) {
            el.classList.remove('drag-over');
            el.removeAttribute('data-drop-position');
            el.removeAttribute('data-dragged-type');
          }
        });

        const y = mouseY - rect.top;
        const position = y < rect.height / 2 ? 'before' : 'after';

        // Add visual indicator with dragged element type
        currentTarget.classList.add('drag-over');
        currentTarget.setAttribute('data-drop-position', position);
        if (draggedData) {
          currentTarget.setAttribute('data-dragged-type', draggedData.type);
        }
        
        setDropTarget({
          element: element,
          position: position
        });
      } catch (error) {
        console.error('Error in handleDragOver:', error);
      }
    }, 16);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only remove if we're not entering a child element
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      e.currentTarget.classList.remove('drag-over');
      e.currentTarget.removeAttribute('data-drop-position');
    }
  };

  const handleDrop = (e, targetElement) => {
    e.preventDefault();
    e.stopPropagation();
    
    e.currentTarget.classList.remove('drag-over');
    e.currentTarget.removeAttribute('data-drop-position');
    e.currentTarget.removeAttribute('data-dragged-type');
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const position = dropTarget?.position || 'after';

      if (data.isNew) {
        // Find all elements at the same level as the target
        const siblingElements = elements.filter(el => el.parentId === targetElement.parentId);
        
        // Find the target's index among its siblings
        const targetIndex = siblingElements.findIndex(el => el.id === targetElement.id);
        
        // Calculate the insertion index in the main elements array
        const mainArrayTargetIndex = elements.findIndex(el => el.id === targetElement.id);
        const insertionIndex = position === 'before' ? mainArrayTargetIndex : mainArrayTargetIndex + 1;

        // Store current elements length to identify the new element
        const currentLength = elements.length;

        // Call handleAddNewElement to create the new element
        handleAddNewElement(data.type);

        // After addNewElement completes, reorder the elements array
        setElements(prevElements => {
          // The new element will be the last one added
          const newElement = prevElements[prevElements.length - 1];
          const elementsWithoutNew = prevElements.slice(0, -1);

          // Create updated elements array with the new element in the correct position
          const updatedElements = [
            ...elementsWithoutNew.slice(0, insertionIndex),
            newElement,
            ...elementsWithoutNew.slice(insertionIndex)
          ];

          // If there's a parent, update its children array
          if (targetElement.parentId) {
            const parentElement = updatedElements.find(el => el.id === targetElement.parentId);
            if (parentElement) {
              const parentChildIndex = parentElement.children.indexOf(targetElement.id);
              const newChildIndex = position === 'before' ? parentChildIndex : parentChildIndex + 1;
              parentElement.children.splice(newChildIndex, 0, newElement.id);
            }
          }

          return updatedElements;
        });

      } else {
        // Handle reordering of existing elements
        const draggedElement = elements.find(el => el.id === data.id);
        if (!draggedElement) return;

        // Create new elements array without the dragged element
        const elementsWithoutDragged = elements.filter(el => el.id !== draggedElement.id);

        // Remove dragged element from its old parent's children array
        if (draggedElement.parentId) {
          const oldParent = elements.find(el => el.id === draggedElement.parentId);
          if (oldParent) {
            oldParent.children = oldParent.children.filter(id => id !== draggedElement.id);
          }
        }

        // Update dragged element's parentId
        draggedElement.parentId = targetElement.parentId;

        // Find insertion index
        const mainArrayTargetIndex = elementsWithoutDragged.findIndex(el => el.id === targetElement.id);
        const insertionIndex = position === 'before' ? mainArrayTargetIndex : mainArrayTargetIndex + 1;

        // Insert dragged element at new position
        const updatedElements = [
          ...elementsWithoutDragged.slice(0, insertionIndex),
          draggedElement,
          ...elementsWithoutDragged.slice(insertionIndex)
        ];

        // Update new parent's children array
        if (targetElement.parentId) {
          const newParent = updatedElements.find(el => el.id === targetElement.parentId);
          if (newParent) {
            const targetIndex = newParent.children.indexOf(targetElement.id);
            const newIndex = position === 'before' ? targetIndex : targetIndex + 1;
            newParent.children.splice(newIndex, 0, draggedElement.id);
          }
        }

        setElements(updatedElements);
        setSelectedElement(draggedElement);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    
    setDropTarget(null);
    setDraggedData(null);
  };

  // Add empty preview drag handlers
  const handleEmptyPreviewDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Add drag-over class to preview-content
    e.currentTarget.classList.add('drag-over');
  };

  const handleEmptyPreviewDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Remove drag-over class from preview-content
    e.currentTarget.classList.remove('drag-over');
  };

  const handleEmptyPreviewDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Remove drag-over class
    e.currentTarget.classList.remove('drag-over');

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.isNew) {
        handleAddNewElement(data.type);
      }
    } catch (error) {
      console.error('Error handling drop on empty preview:', error);
    }
  };

  // Add handleAddNewElement function
  const handleAddNewElement = (type, parentId = null) => {
    // Simply call the addNewElement function from props
    // The newly added element will be tracked via the newlyAddedElement prop
    addNewElement(type, parentId);
  };

  // Initialize custom CSS style tag
  useEffect(() => {
    console.log('currentPageId in useEffect Preview.jsx', currentPageId);
    // Remove any existing style tag with the same ID
    const existingStyle = document.getElementById('custom-css-styles');
    if (existingStyle) {
      document.head.removeChild(existingStyle);
    }

    const styleTag = document.createElement('style');
    styleTag.id = 'custom-css-styles';
    // Add comment in the style tag content instead of as a rule
    styleTag.textContent = '/* Custom CSS Styles */\n';
    document.head.appendChild(styleTag);
    customCssStyleRef.current = styleTag;

    // Initialize CSS for all elements that have custom CSS
    if (elements.length > 0) {
      const allRules = [];
      
      elements.forEach(element => {
        if (element['custom-css']) {
          const builderId = element.builderId;

          // Add desktop styles
          if (element['custom-css'][DEVICES.DESKTOP]) {
            allRules.push(`/* Desktop styles for ${builderId} */
             html body .preview-content {
             [data-builder-id="${builderId}"] {
             ${element['custom-css'][DEVICES.DESKTOP].trim().replace(/\n/g, '\n    ')}
            }
         }`);
          }

          // Add tablet styles with desktop fallback
          const tabletStyles = element['custom-css']?.[DEVICES.TABLET] || element['custom-css']?.[DEVICES.DESKTOP] || '';
          if (tabletStyles) {
            allRules.push(`/* Tablet styles for ${builderId} */
            html body .preview-content[data-device="tablet"] {
           [data-builder-id="${builderId}"] {
           ${tabletStyles.trim().replace(/\n/g, '\n    ')}
           }
          }`);
          }

          // Add mobile styles with tablet and desktop fallback
          const mobileStyles = element['custom-css']?.[DEVICES.MOBILE] || 
                             element['custom-css']?.[DEVICES.TABLET] || 
                             element['custom-css']?.[DEVICES.DESKTOP] || '';
          if (mobileStyles) {
            allRules.push(`/* Mobile styles for ${builderId} */
            html body .preview-content[data-device="mobile"] {
            [data-builder-id="${builderId}"] {
            ${mobileStyles.trim().replace(/\n/g, '\n    ')}
            }
          }`);
          }
        }
      });

      // Join all rules and update the style tag
      if (allRules.length > 0) {
        const fullCssContent = '/* Custom CSS Styles */\n' + allRules.join('\n\n');
        styleTag.textContent = fullCssContent;
      }
    }

    // return () => {
    //   if (customCssStyleRef.current) {
    //     document.head.removeChild(customCssStyleRef.current);
    //   }
    // };
  }, [currentPageId]);

  // Update custom CSS when element changes
  useEffect(() => {
    if (selectedElement && customCssStyleRef.current) {
      try {
        // Get all elements that have custom CSS
        const elementsWithCSS = elements.filter(el => el['custom-css']);
        const allRules = [];

        // Process each element's CSS rules
        elementsWithCSS.forEach(element => {
          const builderId = element.builderId;
          
          // Add desktop styles
          if (element['custom-css'][DEVICES.DESKTOP]) {
            allRules.push(`/* Desktop styles for ${builderId} */
[data-builder-id="${builderId}"] {
  ${element['custom-css'][DEVICES.DESKTOP].trim().replace(/\n/g, '\n  ')}
}`);
          }

          // Add tablet styles with desktop fallback
          const tabletStyles = element['custom-css']?.[DEVICES.TABLET] || element['custom-css']?.[DEVICES.DESKTOP] || '';
          if (tabletStyles) {
            allRules.push(`/* Tablet styles for ${builderId} */
@media (max-width: 1024px) {
  [data-builder-id="${builderId}"] {
    ${tabletStyles.trim().replace(/\n/g, '\n    ')}
  }
}`);
          }

          // Add mobile styles with tablet and desktop fallback
          const mobileStyles = element['custom-css']?.[DEVICES.MOBILE] || 
                             element['custom-css']?.[DEVICES.TABLET] || 
                             element['custom-css']?.[DEVICES.DESKTOP] || '';
          if (mobileStyles) {
            allRules.push(`/* Mobile styles for ${builderId} */
@media (max-width: 768px) {
  [data-builder-id="${builderId}"] {
    ${mobileStyles.trim().replace(/\n/g, '\n    ')}
  }
}`);
          }
        });

        // Join all rules with newlines
        const fullCssContent = '/* Custom CSS Styles */\n\n' + allRules.join('\n\n');

        // Test if the CSS is valid before applying
        const testStyle = document.createElement('style');
        try {
          testStyle.textContent = fullCssContent;
          document.head.appendChild(testStyle);
          document.head.removeChild(testStyle);

          // If we get here, the CSS is valid
          customCssStyleRef.current.textContent = fullCssContent;
          
          // Update the elements array with the new custom CSS
          // setElements(prevElements => {
          //   return prevElements.map(element => {
          //     if (element.id === selectedElement.id) {
          //       return {
          //         ...element,
          //         'custom-css': selectedElement['custom-css']
          //       };
          //     }
          //     return element;
          //   });
          // });
        } catch (error) {
          console.warn('Invalid CSS during typing, waiting for completion...', error);
          return;
        }
      } catch (error) {
        console.warn('Error updating custom CSS:', error);
      }
    }
  }, [selectedElement?.['custom-css'], selectedDevice]);

  // Add data-device attribute to preview-content
  useEffect(() => {
    const previewContent = document.querySelector('.preview-content');
    if (previewContent) {
      previewContent.setAttribute('data-device', selectedDevice.toLowerCase());
    }
  }, [selectedDevice]);

  // Update popup settings when element changes
  useEffect(() => {
    if (settingsPopup.element) {
      const element = settingsPopup.element;
      setPopupSettings({
        text: element.text || '',
        fontSize: element.styles?.fontSize || '',
        fontWeight: element.styles?.fontWeight || '',
        color: element.styles?.color || '',
        textAlign: element.styles?.textAlign || 'left'
      });
    }
  }, [settingsPopup.element]);

  // Move ALL useEffect hooks to the top, including the cleanup one
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .preview-element {
        z-index: 1;
        pointer-events: auto !important;
        cursor: pointer;
      }

      /* Only show hover effect on non-selected elements */
      .preview-element:not(.selected):hover {
        outline: 2px solid rgba(0, 123, 255, 0.4) !important;
        outline-offset: -2px;
      }

      /* Selected element style */
      .preview-element.selected {
        outline: 2px solid #007bff !important;
        outline-offset: -2px;
      }

      /* Settings icon and type label container */
      .preview-element.selected::before,
      .preview-element.selected::after {
        position: absolute;
        top: -24px;
        height: 24px;
        background: #007bff;
        color: white;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        // font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }

      /* Type label */
      .preview-element.selected::after {
        content: attr(data-element-type);
        left: 0;
        padding: 0 8px;
        border-radius: 4px 0 0 0;
        text-transform: uppercase;
        font-weight: 500;
      }

      /* Settings icon - only for elements with editable properties */
      .preview-element.selected[data-element-type="h1"]::before,
      .preview-element.selected[data-element-type="h2"]::before,
      .preview-element.selected[data-element-type="h3"]::before,
      .preview-element.selected[data-element-type="h4"]::before,
      .preview-element.selected[data-element-type="h5"]::before,
      .preview-element.selected[data-element-type="h6"]::before,
      .preview-element.selected[data-element-type="p"]::before,
      .preview-element.selected[data-element-type="button"]::before,
      .preview-element.selected[data-element-type="link"]::before,
      .preview-element.selected[data-element-type="a"]::before,
      .preview-element.selected[data-element-type="text"]::before,
      .preview-element.selected[data-element-type="span"]::before,
      .preview-element.selected[data-element-type="image"]::before,
      .preview-element.selected[data-element-type="cms-content"]::before,
      .preview-element.selected[data-element-type="cart"]::before,
      .preview-element.selected[data-element-type="cart-button"]::before,
      .preview-element.selected[data-element-type="cart-wrapper"]::before,
      .preview-element.selected[data-element-type="link-block"]::before,
      .preview-element.selected[data-element-type="add-to-cart"]::before,
      .preview-element.selected[data-element-type="table"]::before,
      .preview-element.selected[data-element-type="thead"]::before,
      .preview-element.selected[data-element-type="tbody"]::before,
      .preview-element.selected[data-element-type="tr"]::before,
      .preview-element.selected[data-element-type="th"]::before,
      .preview-element.selected[data-element-type="td"]::before {
        content: "⚙";
        width: 24px;
        border-radius: 0 4px 0 0;
        font-size: 14px;
        cursor: pointer;
        border-left: 1px solid rgba(255,255,255,0.2);
        font-weight: normal;
      }

      /* For elements with no settings icon, adjust the type label */
      .preview-element.selected[data-element-type="div"]::after,
      .preview-element.selected[data-element-type="section"]::after {
        border-radius: 4px 4px 0 0;
        border-right: none;
      }

      /* Position settings icon based on type label width */
      .preview-element.selected[data-element-type="button"]::before { left: 60px; }
      .preview-element.selected[data-element-type="h1"]::before,
      .preview-element.selected[data-element-type="h2"]::before,
      .preview-element.selected[data-element-type="h3"]::before,
      .preview-element.selected[data-element-type="h4"]::before,
      .preview-element.selected[data-element-type="h5"]::before,
      .preview-element.selected[data-element-type="h6"]::before { left: 28px; }
      .preview-element.selected[data-element-type="p"]::before { left: 22px; }
      .preview-element.selected[data-element-type="link"]::before,
      .preview-element.selected[data-element-type="a"]::before { left: 40px; }
      .preview-element.selected[data-element-type="text"]::before,
      .preview-element.selected[data-element-type="span"]::before { left: 40px; }
      .preview-element.selected[data-element-type="image"]::before { left: 50px; }
      .preview-element.selected[data-element-type="cms-content"]::before { left: 100px; }
      .preview-element.selected[data-element-type="cart"]::before { left: 40px; }
      .preview-element.selected[data-element-type="cart-button"]::before { left: 100px; }
      .preview-element.selected[data-element-type="cart-wrapper"]::before { left: 105px; }
      .preview-element.selected[data-element-type="link-block"]::before { left: 100px; }
      .preview-element.selected[data-element-type="add-to-cart"]::before { left: 90px; }

      /* Change the content based on element type */
      .preview-element.selected[data-element-type="h1"]::after { content: "H1"; }
      .preview-element.selected[data-element-type="h2"]::after { content: "H2"; }
      .preview-element.selected[data-element-type="h3"]::after { content: "H3"; }
      .preview-element.selected[data-element-type="h4"]::after { content: "H4"; }
      .preview-element.selected[data-element-type="h5"]::after { content: "H5"; }
      .preview-element.selected[data-element-type="h6"]::after { content: "H6"; }
      .preview-element.selected[data-element-type="p"]::after { content: "P"; }
      .preview-element.selected[data-element-type="div"]::after { content: "DIV"; }
      .preview-element.selected[data-element-type="section"]::after { content: "SECTION"; }
      .preview-element.selected[data-element-type="image"]::after { content: "IMAGE"; }
      .preview-element.selected[data-element-type="button"]::after { content: "BUTTON"; }
      .preview-element.selected[data-element-type="link"]::after,
      .preview-element.selected[data-element-type="a"]::after { content: "LINK"; }
      .preview-element.selected[data-element-type="text"]::after,
      .preview-element.selected[data-element-type="span"]::after { content: "TEXT"; }
      .preview-element.selected[data-element-type="link-block"]::after { content: "LINK BLOCK"; }
      
      /* Ensure pseudo-elements are visible */
      .preview-element.selected::before,
      .preview-element.selected::after {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }

      /* Animation Keyframes */
      @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slide-up {
        from {
          transform: translateY(50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes slide-down {
        from {
          transform: translateY(-50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes bounce {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }

      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-10px); }
        75% { transform: translateX(10px); }
      }

      /* Animation classes */
      .preview-element {
        animation-play-state: running;
      }

      .preview-element.selected {
        animation-play-state: paused;
      }

      .preview-element.selected[data-element-type="cart-button"]::before { left: 100px; }
      .preview-element.selected[data-element-type="cart-wrapper"]::before { left: 105px; }
      .preview-element.selected[data-element-type="link-block"]::before { left: 100px; }
      .preview-element.selected[data-element-type="add-to-cart"]::before { left: 90px; }
      
      /* Table element labels */
      .preview-element.selected[data-element-type="table"]::after { content: "TABLE"; }
      .preview-element.selected[data-element-type="thead"]::after { content: "THEAD"; }
      .preview-element.selected[data-element-type="tbody"]::after { content: "TBODY"; }
      .preview-element.selected[data-element-type="tr"]::after { content: "TR"; }
      .preview-element.selected[data-element-type="th"]::after { content: "TH"; }
      .preview-element.selected[data-element-type="td"]::after { content: "TD"; }
      
      /* Position table element settings icons */
      .preview-element.selected[data-element-type="table"]::before { left: 50px; }
      .preview-element.selected[data-element-type="thead"]::before { left: 55px; }
      .preview-element.selected[data-element-type="tbody"]::before { left: 55px; }
      .preview-element.selected[data-element-type="tr"]::before { left: 28px; }
      .preview-element.selected[data-element-type="th"]::before { left: 28px; }
      .preview-element.selected[data-element-type="td"]::before { left: 28px; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    setHasManualResize(false);
  }, [selectedDevice]);

  useEffect(() => {
    if (!containerRef.current || hasManualResize || !elements.length) return;

    const previewSection = containerRef.current.parentElement;
    if (!previewSection) return;
    const previewSectionWidth = previewSection.getBoundingClientRect().width;
    const maxWidth = previewSectionWidth - 40;

    let newWidth;
    switch (selectedDevice) {
      case DEVICES.MOBILE:
        newWidth = Math.min(DEVICE_SIZES.MOBILE, maxWidth);
        break;
      case DEVICES.TABLET:
        newWidth = Math.min(DEVICE_SIZES.TABLET, maxWidth);
        break;
      case DEVICES.DESKTOP:
        newWidth = Math.min(DEVICE_SIZES.DESKTOP, maxWidth);
        break;
      default:
        newWidth = maxWidth;
    }

    containerRef.current.style.width = `${newWidth}px`;
  }, [elements]);

  useEffect(() => {
    if (!containerRef.current) return;

    const previewSection = containerRef.current.parentElement;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        // Add null check to prevent the getBoundingClientRect error
        if (!containerRef.current) return;
        
        const previewSectionWidth = entry.contentRect.width;
        const containerWidth = containerRef.current.getBoundingClientRect().width;
        
        if (containerWidth > previewSectionWidth - 40) {
          containerRef.current.style.width = `${previewSectionWidth - 40}px`;
        }
      }
    });

    resizeObserver.observe(previewSection);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (selectedElement) {
      // console.log('Selected element styles updated:', selectedElement.styles);
    }
  }, [selectedElement?.styles]);

  useEffect(() => {
    // console.log('Elements updated:', elements);
  }, [elements]);

  useEffect(() => {
    // Force re-render when elements or their styles change
    if (elements.length > 0) {
      const forceUpdate = requestAnimationFrame(() => {
        // Force a re-render by updating a ref or state if needed
        if (containerRef.current) {
          containerRef.current.style.minHeight = containerRef.current.style.minHeight;
        }
      });
      return () => cancelAnimationFrame(forceUpdate);
    }
  }, [elements, selectedDevice]);

  useEffect(() => {    
    if (!selectedContentItem || !elements || elements.length === 0) return;
    
    // Force a re-render of the entire preview
    const forceUpdate = requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.style.minHeight = containerRef.current.style.minHeight;
      }
    });
    
    // Find all elements with dynamic fields and update their DOM nodes directly
    elements.forEach(element => {
      if ((element.dynamicField || element.dynamicImageField) && containerRef.current) {
        const elementNode = containerRef.current.querySelector(
          `[data-builder-id="${element.builderId}"]`
        );
        
        if (elementNode) {
          // Update text content for elements with dynamicField
          if (element.dynamicField) {
            const fieldValue = selectedContentItem?.data[element.dynamicField];
            if (fieldValue !== undefined) {
              // For rich text elements, update innerHTML
              if (element.type === 'rich-text' || element.isRichTextContent) {
                elementNode.innerHTML = fieldValue;
              }
              // For input elements, update placeholder
              else if (element.type === 'input' || element.type === 'textarea') {
                elementNode.placeholder = fieldValue;
              }
              // For all other elements, update textContent
              else {
                elementNode.textContent = fieldValue;
              }
            }
          }
          
          // Update image src for elements with dynamicImageField
          if (element.type === 'image' && element.dynamicImageField) {
            const imageUrl = selectedContentItem?.data[element.dynamicImageField];
            if (imageUrl) {
              elementNode.src = imageUrl;
            } else {
              elementNode.src = "https://placehold.co/150x150";
            }
          }
        }
      }
    });
    
    return () => cancelAnimationFrame(forceUpdate);
  }, [selectedContentItem, elements]);

  // Update the effect that handles selected element style updates
 /* useEffect(() => {
    if (selectedElement && selectedElement.styles) {
      const element = elements.find(el => el.id === selectedElement.id);
      if (element && containerRef.current) {
        // Force immediate style update
        const elementNode = containerRef.current.querySelector(
          `[data-builder-id="${element.builderId}"]`
        );
        if (elementNode) {
          const processedStyles = processStyles(selectedElement.styles);
          // Apply each style individually to ensure updates
          Object.entries(processedStyles).forEach(([key, value]) => {
            elementNode.style[key] = value;
          });
        }
      }
    }
  }, [selectedElement?.styles, elements]); */

  // Add a new effect to handle element style updates
/*  useEffect(() => {
    elements.forEach(element => {
      if (containerRef.current) {
        const elementNode = containerRef.current.querySelector(
          `[data-builder-id="${element.builderId}"]`
        );
        if (elementNode) {
          const processedStyles = processStyles(element.styles);
          Object.entries(processedStyles).forEach(([key, value]) => {
            elementNode.style[key] = value;
          });
        }
      }
    });
  }, [elements]); */

  // Add findElementById helper function
  const findElementById = (id) => {
    return elements.find(el => el.id === id);
  };

  const startResizing = (e, direction) => {
    e.preventDefault();
    setHasManualResize(true);
    
    // Reset state to default if there's a selected element using updateElementState
    if (selectedElement) {
      updateElementState(selectedElement.id, selectedElement.currentState, false);
    }
    
    if (!containerRef.current) return;
    
    resizeRef.current = {
      startX: e.pageX,
      startWidth: containerRef.current.getBoundingClientRect().width,
      direction
    };

    let rafId = null;

    function handleMouseMove(e) {
      if (rafId) cancelAnimationFrame(rafId);
      
      rafId = requestAnimationFrame(() => {
        const { startX, startWidth, direction } = resizeRef.current;
        const currentX = e.pageX;
        const diff = direction === 'right' ? currentX - startX : startX - currentX;
        
        if (!containerRef.current || !containerRef.current.parentElement) return;
        const previewSectionWidth = containerRef.current.parentElement.getBoundingClientRect().width;
        const maxWidth = previewSectionWidth - 40;
        
        const newWidth = Math.max(320, Math.min(maxWidth, startWidth + diff));
        containerRef.current.style.width = `${newWidth}px`;

        if (newWidth <= DEVICE_SIZES.MOBILE) {
          setSelectedDevice(DEVICES.MOBILE);
        } else if (newWidth <= DEVICE_SIZES.TABLET) {
          setSelectedDevice(DEVICES.TABLET);
        } else if (newWidth <= DEVICE_SIZES.DESKTOP) {
          setSelectedDevice(DEVICES.DESKTOP);
        } else {
          setSelectedDevice(DEVICES.CUSTOM);
        }
      });
    }

    function handleMouseUp() {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleElementClick = (e, element) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if the element type has quick settings
    const hasQuickSettings = [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 
      'button', 'a', 'link', 'link-block', 'text', 'span', 'image', 'cms-content',
      'cart', 'cart-button', 'cart-wrapper', 'add-to-cart',
      'rich-text', 'dropdown', 'table', 'th', 'td'
    ].includes(element.type);
    
    // Calculate the width of the type label based on element type
    let labelWidth = 28; // Default width for h1-h6
    if (element.type === 'p') labelWidth = 22;
    else if (element.type === 'button') labelWidth = 52;
    else if (element.type === 'link' || element.type === 'a') labelWidth = 40;
    else if (element.type === 'link-block') labelWidth = 90;
    else if (element.type === 'text' || element.type === 'span') labelWidth = 40;
    else if (element.type === 'image') labelWidth = 50;
    else if (element.type === 'cms-content') labelWidth = 90;
    else if (element.type === 'cart') labelWidth = 40;
    else if (element.type === 'cart-button') labelWidth = 105;
    else if (element.type === 'cart-wrapper') labelWidth = 105;
    else if (element.type === 'add-to-cart') labelWidth = 90;
    else if (element.type === 'dropdown') labelWidth = 70;
    else if (element.type === 'table') labelWidth = 50;
    else if (element.type === 'thead') labelWidth = 55;
    else if (element.type === 'tbody') labelWidth = 55;
    else if (element.type === 'tr') labelWidth = 28;
    else if (element.type === 'th') labelWidth = 28;
    else if (element.type === 'td') labelWidth = 28;
    
    // Check if click was on the settings icon (::before pseudo-element)
    const rect = e.currentTarget.getBoundingClientRect();
    
    // Only check for settings icon clicks if the element has quick settings
    if (hasQuickSettings) {
      const isSettingsIconClick = 
        e.clientX > rect.left + labelWidth && 
        e.clientX < rect.left + labelWidth + 24 && // Width of settings icon
        e.clientY < rect.top + 5;
      
      if (isSettingsIconClick && selectedElement?.id === element.id) {
        // Toggle settings popup
        setSettingsPopup({
          isOpen: !settingsPopup.isOpen,
          element: element,
          position: {
            top: rect.top - 5,
            left: rect.left
          }
        });
        return;
      }
    }
    
    // Regular element selection
    setSelectedElement(element);
  };

  // Move processStyles outside of renderElement so it can be used by effects
  const processStyles = (styles) => {
    if (!styles || typeof styles !== 'object') return {};

    // Clean up any numeric keys first
    const cleanStyles = Object.fromEntries(
      Object.entries(styles).filter(([key]) => isNaN(parseInt(key)))
    );

    const processed = {};
    Object.entries(cleanStyles).forEach(([key, value]) => {
      // Skip if value is a Map or undefined
      if (value instanceof Map || value === undefined) return;

      // Handle special cases for React inline styles
      switch(key) {
        case 'background-color':
          processed.backgroundColor = value;
          break;
        case 'font-size':
          processed.fontSize = value;
          break;
        case 'font-weight':
          processed.fontWeight = value;
          break;
        case 'text-align':
          processed.textAlign = value;
          break;
        case 'line-height':
          processed.lineHeight = value;
          break;
        case 'padding-top':
          processed.paddingTop = value;
          break;
        case 'padding-right':
          processed.paddingRight = value;
          break;
        case 'padding-bottom':
          processed.paddingBottom = value;
          break;
        case 'padding-left':
          processed.paddingLeft = value;
          break;
        case 'margin-top':
          processed.marginTop = value;
          break;
        case 'margin-right':
          processed.marginRight = value;
          break;
        case 'margin-bottom':
          processed.marginBottom = value;
          break;
        case 'margin-left':
          processed.marginLeft = value;
          break;
        case 'border-radius':
          processed.borderRadius = value;
          break;
        case 'min-height':
          processed.minHeight = value;
          break;
        case 'min-width':
          processed.minWidth = value;
          break;
        case 'max-width':
          processed.maxWidth = value;
          break;
        case 'height':
          processed.height = value;
          break;
        case 'width':
          processed.width = value;
          break;
        case 'display':
          processed.display = value;
          break;
        case 'position':
          processed.position = value;
          break;
        default:
          // Convert kebab-case to camelCase for other properties
          const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          processed[camelKey] = value;
      }
    });

    return processed;
  };

  const getAnimationStyles = (element) => {
    if (!element.interactions?.events) return {};

    const animationEvents = element.interactions.events.filter(event => event.type === 'animation');
    if (!animationEvents.length) return {};

    // Combine all animations
    const animations = animationEvents.map(event => ({
      name: event.value.animation,
      duration: event.value.duration,
      delay: event.value.delay,
      iterations: event.value.iterations
    }));

    // Create animation CSS properties
    const animationProps = animations.map(anim => 
      `${anim.name} ${anim.duration} ${anim.delay} ${anim.iterations === 'infinite' ? 'infinite' : anim.iterations}`
    ).join(', ');

    return {
      animation: animationProps,
      animationFillMode: 'both'
    };
  };

  const renderElement = (element) => {
    if (!element) return null;

    // Handle slider elements
    if (element.sliderRole) {
      const styles = processStyles(getElementStyles(element, classStylesRegistry, combinedClassStylesRegistry, selectedDevice));
      const sliderCommonProps = {
        id: element.builderId,
        'data-builder-id': element.builderId,
        style: styles,
        onClick: (e) => handleElementClick(e, element),
        className: `preview-element ${element.className || ''} ${
          selectedElement?.id === element.id ? 'selected' : ''
        }`,
        onDragOver: (e) => handleDragOver(e, element),
        onDragLeave: handleDragLeave,
        onDrop: (e) => handleDrop(e, element),
        // Add data-force-state attribute if element has a forceState
        ...(element.forceState && { 'data-force-state': element.forceState })
      };

      // Handle different slider roles without switch statement
      if (element.sliderRole === 'container') {
        // Get the appropriate device settings based on selectedDevice
        const deviceConfig = element.deviceSettings?.[selectedDevice] || {};
        const slidesPerView = deviceConfig.slidesPerView || element.slidesPerView || 1;
        const slidesPerScroll = deviceConfig.slidesPerScroll || element.slidesPerScroll || 1;
        
        return (
          <div
            {...sliderCommonProps}
            data-slider-role="container"
            data-element-type="slider"
            data-slides-per-view={slidesPerView}
            data-slides-per-scroll={slidesPerScroll}
          >
            {element.children?.map(childId => renderElement(findElementById(childId)))}
          </div>
        );
      }

      if (element.sliderRole === 'mask') {
        // Find the parent slider to get the slidesPerView
        const parentSlider = findElementById(element.parentId);
        
        // Get device-specific slidesPerView
        const deviceConfig = parentSlider?.deviceSettings?.[selectedDevice] || {};
        const slidesPerView = deviceConfig.slidesPerView || parentSlider?.slidesPerView || 1;
        
        // Adjust flex basis of slides based on slidesPerView
        const updatedChildren = element.children?.map(childId => {
          const slideElement = findElementById(childId);
          if (slideElement && slideElement.sliderRole === 'slide') {
            return {
              ...slideElement,
              styles: {
                ...slideElement.styles,
                flex: `0 0 ${100/slidesPerView}%`
              }
            };
          }
          return slideElement;
        });
        
        return (
          <div
            {...sliderCommonProps}
            data-slider-role="mask"
            data-current-slide="0"
            data-slides-count={element.children?.length || 0}
          >
            {updatedChildren?.map(childElement => renderElement(childElement))}
          </div>
        );
      }

      if (element.sliderRole === 'slide') {
        return (
          <div
            {...sliderCommonProps}
            data-slider-role="slide"
          >
            {element.text}
            {element.children?.map(childId => renderElement(findElementById(childId)))}
          </div>
        );
      }

      if (element.sliderRole === 'arrow') {
        return (
          <a
            {...sliderCommonProps}
            data-slider-role="arrow"
            data-direction={element.direction}
            // href="#"
            onClick={(e) => e.preventDefault()}
          >
            {element.children?.map(childId => renderElement(findElementById(childId)))}
          </a>
        );
      }

      return null;
    }

    // Keep existing dynamic content handling
    let displayText = element.text || '';  
    let displaySrc = element.src;
    let isRichContent = element.type === 'rich-text' || element.isRichTextContent;

    if (element.selectedContentItem && element.dynamicField) {
      const dynamicContent = element.selectedContentItem.data[element.dynamicField];
      if (dynamicContent !== undefined) {
        if (element.type === 'image') {
          displaySrc = dynamicContent;
        } else {
          displayText = dynamicContent;
        }
      }
    } else if (element.text && element.text.includes('{{')) {
      displayText = element.text.replace(/\{\{(.*?)\}\}/g, (match, field) => {
        const fieldName = field.trim();
        const value = element.selectedContentItem?.data[fieldName];
        return value !== undefined ? value : match;
      });
    }

    // Update the commonProps to use the new getElementStyles
    const commonProps = {
      className: `preview-element ${element.className || ''} ${
        selectedElement?.id === element.id ? 'selected' : ''
      }`,
      onClick: (e) => handleElementClick(e, element),
      style: {
        ...processStyles(getElementStyles(element, classStylesRegistry, combinedClassStylesRegistry, selectedDevice)),
        ...getAnimationStyles(element)
      },
      id: element.id,
      draggable: true,
      onDragEnter: (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Drag enter:', element.type);
        handleDragOver(e, element);
      },
      onDragOver: (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleDragOver(e, element);
      },
      onDragLeave: (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Drag leave:', element.type);
        handleDragLeave(e);
      },
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Drop:', element.type);
        handleDrop(e, element);
      },
      'data-builder-id': element.builderId,
      'data-element-type': element.type,
      // Add data-force-state attribute if element has a forceState
      ...(element.forceState && { 'data-force-state': element.forceState }),
      onDragStart: (e) => {
        e.stopPropagation();
        // Set data for existing element
        e.dataTransfer.setData('application/json', JSON.stringify({
          id: element.id,
          type: element.type,
          isNew: false
        }));
      }
    };

    // Add slider-specific styles
    if (element.className?.includes('slider-slide')) {
      commonProps.style = {
        ...commonProps.style,
        flex: '0 0 100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      };
    }

    // Add slider arrow styles
    if (element.className?.includes('slider-arrow')) {
      commonProps.style = {
        ...commonProps.style,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [element.className.includes('left') ? 'left' : 'right']: '20px',
        padding: '10px 15px',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        fontSize: '18px',
        zIndex: 1
      };
    }

    // Update validTypes array
    const validTypes = [
      'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'button', 'img', 'ul', 'li', 'a',
      'input', 'section', 'span', 'video', 'label',
      'textarea', 'form', 'slider', 'cart', 'cart-button', 'cart-wrapper', 'add-to-cart',
      'quantity-minus', 'quantity-plus', 'remove-button', 'checkout-order-item', 'checkout-submit-button',
      'rich-text', 'map', 'embed', 'dropdown', 'option', 'select', 'nav',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'tab-button', 'checkbox', 'radio' // Add table-related HTML elements
    ];

    // Update typeMap
    const typeMap = {
      'text': 'span',
      'link': 'a',
      'link-block': 'a',
      'image': 'img',
      'list': 'ul',
      'listItem': 'li',
      'cms-content': 'div',
      'submit': 'button',
      'textarea': 'textarea',
      'label': 'label',
      'form': 'form',
      'slider': 'div',
      'checkbox': 'input',
      'radio': 'input',
      'cart': 'div',
      'cart-button': 'button',
      'cart-wrapper': 'div',
      'add-to-cart': 'button',
      'cart-content': 'div',
      'quantity-minus': 'button',
      'quantity-plus': 'button',
      'remove-button': 'button',
      'checkout-order-item': 'div',
      'checkout-submit-button': 'button',
      'rich-text': 'div',
      'map': 'div',
      'embed': 'div',
      'dropdown': 'select',
      'option': 'option',
      'nav': 'nav',
      'table': 'table',
      'thead': 'thead',
      'tbody': 'tbody',
      'tr': 'tr',
      'th': 'th',
      'td': 'td',
      'tab-button': 'button'
    };

    // Get the actual HTML element type to use
    const elementType = typeMap[element.type] || element.type;

    if (!validTypes.includes(elementType)) {
      console.error(`Invalid element type: ${element.type}, mapped to: ${elementType}`);
      return null;
    }

    switch (element.type) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        const HeadingTag = element.type;
        return (
          <HeadingTag {...commonProps}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement({
                ...childElement,
                selectedContentItem: element.selectedContentItem
              }) : null;
            })}
          </HeadingTag>
        );
      
      case 'rich-text':
        // Handle dynamic content for rich text elements
        let richTextContent = element.text || '<p>Rich text content</p>';
        
        // Check if this is a template with dynamic content
        if (element.selectedContentItem && element.dynamicField) {
          const dynamicContent = element.selectedContentItem.data[element.dynamicField];
          // Use dynamic content if it exists
          if (dynamicContent !== undefined) {
            richTextContent = dynamicContent;
          }
        }
        
        return (
          <div 
            {...commonProps}
            data-element-type="rich-text"
            dangerouslySetInnerHTML={{ __html: richTextContent }}
          />
        );

      case 'image':
        return (
          <img 
            {...commonProps} 
            src={element.src || displaySrc || "https://placehold.co/150x150"}
            alt={element.text || displayText || "Image"}
            data-element-type="image"
          />
        );

      case 'video':
        const videoSrc = displaySrc || element.videoSrc || "https://www.youtube.com/embed/dQw4w9WgXcQ";
        const videoType = element.videoType || 'youtube';
        let embedSrc = '';
        
        // Handle different video platforms
        if (videoType === 'youtube') {
          // Extract YouTube video ID if full URL is provided
          const youtubeId = videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be') 
            ? videoSrc.replace(/.*(?:youtu.be\/|v\/|\/v\/|\/embed\/|\/watch\?v=)([^#\&\?]*).*/, '$1')
            : videoSrc;
          embedSrc = `https://www.youtube.com/embed/${youtubeId}`;
        } else if (videoType === 'vimeo') {
          // Extract Vimeo video ID if full URL is provided
          const vimeoId = videoSrc.includes('vimeo.com')
            ? videoSrc.replace(/.*(?:vimeo.com\/)([^#\&\?]*).*/, '$1')
            : videoSrc;
          embedSrc = `https://player.vimeo.com/video/${vimeoId}`;
        } else {
          // Default to treating as direct embed URL
          embedSrc = videoSrc;
        }
        
        // Add query parameters based on settings
        const autoplay = element.autoplay ? '1' : '0';
        const controls = element.controls !== false ? '1' : '0';
        const loop = element.loop ? '1' : '0';
        const mute = element.mute ? '1' : '0';
        
        // Add parameters based on video type
        if (videoType === 'youtube') {
          embedSrc += `?autoplay=${autoplay}&controls=${controls}&loop=${loop}&mute=${mute}`;
        } else if (videoType === 'vimeo') {
          embedSrc += `?autoplay=${autoplay}&loop=${loop}&muted=${mute}`;
        }
        
        return (
          <div {...commonProps} data-element-type="video" data-video-type={videoType}>
            <iframe 
              src={embedSrc}
              width="100%" 
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );

      case 'embed':
        // In the builder view, just show a placeholder for the embed code
        return (
          <div {...commonProps} data-element-type="embed">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              textAlign: 'center',
              padding: '15px',
              backgroundColor: '#f8f8f8',
              border: '1px dashed #aaa',
              borderRadius: '4px'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '10px' }}>📝</div>
              <div>
                <strong style={{ fontSize: '16px' }}>Embed Code Element</strong>
                <p style={{ fontSize: '14px', color: '#555', margin: '10px 0 5px' }}>
                  {element.embedCode ? 'Custom code will appear here when published' : 'Add embed code in the properties panel'}
                </p>
                {element.embedCode && (
                  <div style={{ 
                    marginTop: '10px',
                    padding: '8px', 
                    backgroundColor: '#e9e9e9', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#666',
                    maxWidth: '90%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {element.embedCode.length > 50 
                      ? element.embedCode.substring(0, 50) + '...' 
                      : element.embedCode}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'list':
        return (
          <ul {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </ul>
        );

      case 'listItem':
        return (
          <li {...commonProps}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </li>
        );

      case 'link':
        return (
          <a {...commonProps} href="#" onClick={(e) => e.preventDefault()}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </a>
        );

      case 'link-block':
        return (
          <a 
            {...commonProps} 
            href="#" 
            onClick={(e) => e.preventDefault()}
            style={{
              ...commonProps.style,
              display: 'block'
            }}
          >
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </a>
        );

      case 'input':
        return (
          <input 
            {...commonProps} 
            type={element.inputType || 'text'} 
            name={element.name}
            placeholder={element.placeholder || displayText} 
            required={element.required}
            autoFocus={element.autofocus}
            readOnly={true}
            onFocus={(e) => {
              e.target.select();
              handleElementClick(e, element);
            }}
          />
        );

      case 'checkbox':
        return <input {...commonProps} type="checkbox" />;

      case 'radio':
        return <input {...commonProps} type="radio" />;

      case 'grid':
        return (
          <div {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </div>
        );

      case 'cms-content':
        const contentType = cmsContentTypes?.find(type => type._id === element.contentTypeId);
        const content = cmsContent?.[element.contentTypeId] || [];
        
        return (
          <div {...commonProps}>
            <CMSContentElement
              contentTypeId={element.contentTypeId}
              contentType={contentType}
              content={content}
              displayType={element.displayType || 'list'}
              limit={element.limit || 10}
              sortBy={element.sortBy || 'createdAt'}
              sortOrder={element.sortOrder || 'desc'}
            >
              {(contentItem) => (
                element.children?.map(childId => {
                  const childElement = elements.find(el => el.id === childId);
                  return childElement ? renderElement({
                    ...childElement,
                    key: `${childId}-${contentItem._id}`,
                    selectedContentItem: contentItem
                  }) : null;
                })
              )}
            </CMSContentElement>
          </div>
        );

      case 'label':
        return (
          <label {...commonProps}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </label>
        );

      case 'textarea':
        return (
          <textarea 
            {...commonProps}
            placeholder={displayText}
            rows={4}
            readOnly={true}
            onFocus={(e) => {
              e.target.select();
              handleElementClick(e, element);
            }}
          />
        );

      case 'dropdown':
        return (
          <select 
            {...commonProps}
            disabled={true}
            onClick={(e) => {
              e.preventDefault();
              handleElementClick(e, element);
            }}
          >
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </select>
        );

      case 'option':
        return (
          <option 
            value={element.value || ''}
            {...commonProps}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleElementClick(e, element);
            }}
          >
            {element.text || 'Option'}
          </option>
        );

      case 'submit':
        return (
          <button 
            {...commonProps}
            type="submit"
          >
            {displayText || 'Submit'}
          </button>
        );

      case 'form':
        return (
          <form 
            {...commonProps}
            name={element.name === undefined ? 'Email Form' : element.name}
            method={element.method || 'get'}
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </form>
        );

      case 'cart':
        return (
          <div 
            {...commonProps}
            {...element.attributes}
          >
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </div>
        );

      case 'cart-button':
        return (
          <button 
            {...commonProps}
            {...element.attributes}
          >
            {element.text}
          </button>
        );

      case 'cart-wrapper':
        return (
          <div
            {...commonProps}
            className={`${element.className || ''} ${selectedElement?.id === element.id ? 'selected-element' : ''}`}
            {...element.attributes}
          >
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </div>
        );

      case 'add-to-cart':
        return (
          <button
            {...commonProps}
            className={`${element.className || ''} ${selectedElement?.id === element.id ? 'selected-element' : ''}`}
            {...element.attributes}
          >
            {element.text || 'Add to Cart'}
          </button>
        );

      case 'cart-content':
        return (
          <div 
            {...commonProps}
            data-element="cart-content"
            role="list"
            aria-label="Cart items"
          >
            {element.text}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return renderElement(childElement);
            })}
          </div>
        );

      case 'quantity-minus':
        return (
          <button 
            {...commonProps}
            type="button"
            aria-label="Decrease quantity"
          >
            {displayText || '-'}
          </button>
        );

      case 'quantity-plus':
        return (
          <button 
            {...commonProps}
            type="button"
            aria-label="Increase quantity"
          >
            {displayText || '+'}
          </button>
        );

      case 'remove-button':
        return (
          <button 
            {...commonProps}
            type="button"
            aria-label="Remove item from cart"
          >
            {displayText || '×'}
          </button>
        );

      case 'checkout-order-item':
        return (
          <div {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </div>
        );

      case 'checkout-submit-button':
        return (
          <button {...commonProps}>
            {displayText || 'Complete Purchase'}
          </button>
        );

      case 'map':
        // Default to a central location if none specified
        const mapLocation = element.mapLocation || 'New York, NY';
        const mapZoom = element.mapZoom || '13';
        const mapQuery = encodeURIComponent(mapLocation);
        const mapSrc = `https://maps.google.com/maps?q=${mapQuery}&t=&z=${mapZoom}&ie=UTF8&iwloc=&output=embed`;
        
        return (
          <div {...commonProps} data-element-type="map">
            <iframe 
              title="Google Map"
              src={mapSrc}
              width="100%" 
              height="100%" 
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        );

      case 'nav':
        return (
          <nav {...commonProps}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement({
                ...childElement,
                selectedContentItem: element.selectedContentItem
              }) : null;
            })}
          </nav>
        );

      case 'table':
        return (
          <table {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </table>
        );

      case 'thead':
        return (
          <thead {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </thead>
        );

      case 'tbody':
        return (
          <tbody {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </tbody>
        );

      case 'tr':
        return (
          <tr {...commonProps}>
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </tr>
        );

      case 'th':
        return (
          <th {...commonProps}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </th>
        );

      case 'td':
        return (
          <td {...commonProps}>
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement(childElement) : null;
            })}
          </td>
        );

      case 'tab-button':
        return (
          <button {...commonProps}>
            {displayText}
          </button>
        );

      default:
        const Tag = ['add-to-cart'].includes(elementType) ? 'button' : elementType;
        const hasSliderMask = element.className?.includes('slider-mask');
        const style = {
          ...commonProps.style,
          ...(hasSliderMask && {
            position: 'absolute',
            display: 'flex',
            width: '100%',
            height: '100%',
            transition: 'transform 0.5s ease-in-out'
          })
        };

        // For elements with rich text content but not 'rich-text' type
        if (isRichContent && element.type !== 'rich-text') {
          return (
            <Tag 
              {...commonProps}
              style={style}
              data-current-slide={hasSliderMask ? "0" : undefined}
              dangerouslySetInnerHTML={{ __html: displayText }}
            />
          );
        }

        return (
          <Tag 
            {...commonProps}
            style={style}
            data-current-slide={hasSliderMask ? "0" : undefined}
          >
            {displayText}
            {element.children?.map(childId => {
              const childElement = elements.find(el => el.id === childId);
              return childElement ? renderElement({
                ...childElement,
                selectedContentItem: element.selectedContentItem
              }) : null;
            })}
          </Tag>
        );
    }
  };

  // Move the cleanup effect here, before any conditional logic
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Close settings popup when selected element changes
    setSettingsPopup(prev => ({...prev, isOpen: false}));
  }, [selectedElement]);


  // Add or update stylesheet creation in useEffect
  useEffect(() => {
    // Remove existing stylesheets first
    if (previewStyleSheetRef && document.head.contains(previewStyleSheetRef)) {
      document.head.removeChild(previewStyleSheetRef);
      previewStyleSheetRef = null;
    }
    if (previewCustomCssStyleSheetRef && document.head.contains(previewCustomCssStyleSheetRef)) {
      document.head.removeChild(previewCustomCssStyleSheetRef);
      previewCustomCssStyleSheetRef = null;
    }

    // Get current page data
    const currentPage = pages.find(page => page.id === currentPageId);
    
    if (currentPage) {
      // Create new stylesheets using the current page's style registries
      createPreviewStyleSheet(
        currentPage.classStylesRegistry, 
        currentPage.combinedClassStylesRegistry
      );
      createPreviewCustomCssStyleSheet(currentPage.elements);
    }
  }, [currentPageId]);

  // Update stylesheet creation in useEffect (without cleanup)
  useEffect(() => {
  /*  const currentPage = pages.find(page => page.id === currentPageId);
    if (currentPage) {
      console.log('currentPage in useEffect', currentPage);
      createPreviewStyleSheet(
        currentPage.classStylesRegistry, 
        currentPage.combinedClassStylesRegistry
      );
      createPreviewCustomCssStyleSheet(currentPage.elements);
    } */

      createPreviewStyleSheet(
        classStylesRegistry, 
        combinedClassStylesRegistry
      );
      createPreviewCustomCssStyleSheet(elements);
  }, [classStylesRegistry, combinedClassStylesRegistry]);

  // Update device change effect (without cleanup)
  useEffect(() => {
    const previewContent = document.querySelector('.preview-content');
    if (previewContent) {
      previewContent.setAttribute('data-device', selectedDevice.toLowerCase());
    }
  }, [selectedDevice, pages, currentPageId]); 

  // Add this event listener to the document
  useEffect(() => {
    const handleDragStart = (e) => {
      try {
        const data = JSON.parse(e.dataTransfer.getData('application/json'));
        setDraggedData(data);
      } catch (error) {
        // Ignore error as this might not be our drag operation
      }
    };

    document.addEventListener('dragstart', handleDragStart);
    return () => document.removeEventListener('dragstart', handleDragStart);
  }, []);

  // Add this useEffect to watch for newly added elements
  useEffect(() => {
    // If there's a newly added element ID and elements have been updated
    if (newlyAddedElementId && elements.length > 0) {
      console.log('newlyAddedElementId', newlyAddedElementId);

      // Find the element
      const element = elements.find(el => el.id === newlyAddedElementId);
      
      // Check if it's an element type that has quick settings
      if (element && [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 
        'button', 'a', 'link', 'text', 'span', 'image', 'cms-content',
        'cart', 'cart-button', 'cart-wrapper', 'add-to-cart',
        'rich-text', 'dropdown', 'table', 'th', 'td'
      ].includes(element.type)) {
        // Open settings popup for this element
        setSettingsPopup({
          isOpen: true,
          element: element,
          position: {
            top: 100, // Default position until element is rendered
            left: 100
          }
        });
        
        // Set the selected element
        // setSelectedElement(element);
        
        // Wait for element to be rendered, then update popup position
        setTimeout(() => {
          const elementNode = document.querySelector(`[data-builder-id="${element.builderId}"]`);
          if (elementNode) {
            const rect = elementNode.getBoundingClientRect();
            setSettingsPopup(prev => ({
              ...prev,
              position: {
                top: rect.top - 5,
                left: rect.left
              }
            }));
          }
        }, 100);
      }
      
      // Reset the newly added element ID
      setNewlyAddedElementId(null);
    }
  }, [newlyAddedElementId, elements]);

  // Update the useEffect that handles newly added elements
  useEffect(() => {
    if (newlyAddedElement) {
      console.log('New element added:', newlyAddedElement.type);
      
      // If it's a cart element, set it as the selected element
      if (newlyAddedElement.type === 'cart') {
        setSelectedElement(newlyAddedElement);
      }
      
      // Skip showing popup for rich-text elements - we have a better editor in main container now
      if (newlyAddedElement.type === 'rich-text') {
        // Just select the element - the new rich text editor will be shown by the Setting component
        setSelectedElement(newlyAddedElement);
        
        // Clear the newly added element reference without showing the popup
        setNewlyAddedElement(null);
      } 
      // Check if it's an element type that has quick settings (not rich-text)
      else if ([
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 
        'button', 'a', 'link', 'text', 'span', 'image', 'cms-content',
        'cart', 'cart-button', 'cart-wrapper', 'add-to-cart',
        'dropdown', 'table', 'th', 'td'
      ].includes(newlyAddedElement.type)) {
        // Give the DOM a moment to render the new element
        setTimeout(() => {
          const elementNode = document.querySelector(`[data-builder-id="${newlyAddedElement.builderId}"]`);
          if (elementNode) {
            const rect = elementNode.getBoundingClientRect();
            
            setSettingsPopup({
              isOpen: true,
              element: newlyAddedElement,
              position: {
                top: rect.top - 5,
                left: rect.left
              }
            });
          } else {
            // Fallback position if element not found in DOM
            setSettingsPopup({
              isOpen: true,
              element: newlyAddedElement,
              position: {
                top: 100,
                left: 100
              }
            });
          }
          
          // Clear the newly added element reference after showing the popup
          setNewlyAddedElement(null);
        }, 100);
      } else {
        // If the element doesn't have quick settings, still clear the reference
        setNewlyAddedElement(null);
      }
    }
  }, [newlyAddedElement, setNewlyAddedElement, setSelectedElement]);

  const rootElements = getRootElements();
  if (!rootElements) return null;

  return (
    <div className="preview-section">
      <div className="preview-container" ref={containerRef}>
        <div className="resize-handles-container">
          {/* <div 
            className="resize-handle left"
            onMouseDown={(e) => startResizing(e, 'left')}
          /> */}
          <div 
            className="resize-handle right"
            onMouseDown={(e) => startResizing(e, 'right')}
          />
        </div>
        <div 
          className="preview-content"
          onDragOver={handleEmptyPreviewDragOver}
          onDragLeave={handleEmptyPreviewDragLeave}
          onDrop={handleEmptyPreviewDrop}
          style={{ 
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'top left',
            height: zoomLevel < 1 ? `${100/zoomLevel}%` : '100%',
            width: zoomLevel < 1 ? `${100/zoomLevel}%` : '100%'
          }}
        >
          {Array.isArray(rootElements) && rootElements.length > 0
            ? rootElements.map(element => renderElement(element))
            : null
          }
          {settingsPopup.isOpen && (
            <ElementSettingsPopup
              isOpen={settingsPopup.isOpen}
              element={settingsPopup.element}
              onClose={() => setSettingsPopup(prev => ({ ...prev, isOpen: false }))}
              updateElementText={updateElementText}
              setShowAssetsSidebar={setShowAssetsSidebar}
              cmsContentTypes={cmsContentTypes}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Preview;
